import { forwardRef } from "react"
import {
  Link as ReactRouterLink,
  type LinkProps as ReactRouterLinkProps,
} from "react-router-dom"

interface LinkProps extends Omit<ReactRouterLinkProps, "state"> {
  /**
   * By default, our routing restores the scroll position of each screen as it was the last time it was was shown.
   * If you want to opt-out of this, perhaps because this is a screen that shows important controls at the top, provide this attribute.
   */
  scrollToTop?: boolean
  /**
   * If true, the link is not rendered as a React Router Link,
   * but as a regular anchor element without href,
   * therefore it is not focusable and not clickable.
   */
  disabled?: boolean
}

/**
 * React Router Link component with optional disabled state and scroll to top state.
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  function Link(props, ref) {
    const {
      disabled,
      scrollToTop = false,

      to,
      reloadDocument,
      replace,
      preventScrollReset,
      relative,
      unstable_viewTransition,

      ...otherProps
    } = props

    if (disabled) {
      return <a ref={ref} {...otherProps} />
    }

    const state: LinkState = { scrollToTop }

    const reactRouterProps = {
      to,
      reloadDocument,
      replace,
      state,
      preventScrollReset,
      relative,
      unstable_viewTransition,
    }

    return <ReactRouterLink ref={ref} {...reactRouterProps} {...otherProps} />
  },
)

interface LinkState {
  scrollToTop: boolean
}

export function isLinkState(obj: unknown): obj is LinkState {
  return !!obj && typeof obj === "object" && "scrollToTop" in obj
}
